import { prepareMathMarkdown } from "@/utils/math";
import "katex/dist/katex.min.css";
import { useCallback, useMemo, useRef } from "react";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { useChatSession } from "../context/ChatSessionProvider";
import { useCitations } from "../context/CitationProvider";
import { useMapProgress } from "../hooks/useMapProgress";
import type { TraceTurn } from "../types";
import {
  cleanTextForDisplay,
  getTurnCitationText,
  type Citation,
} from "../utils/citations";
import { MessageActionsBar } from "./actions/MessageActionsBar";
import {
  ChatMarkdownProvider,
  chatMarkdownComponents,
} from "./chatMarkdownComponents";
import { ResponseSkeleton } from "./StatusIndicators";
import { ToolCallItem } from "./ToolCallItem";

const ChatMarkdown = ({ source }: { source: string }) => (
  <Markdown
    remarkPlugins={[
      remarkGfm,
      [remarkMath, { singleDollarTextMath: true }],
    ]}
    rehypePlugins={[rehypeRaw, rehypeSanitize, rehypeKatex]}
    components={chatMarkdownComponents}
  >
    {source}
  </Markdown>
);

interface Props {
  turn: TraceTurn;
  isLastInGroup?: boolean;
  /** Citations for the whole assistant response group (not just this turn). */
  groupCitations?: Citation[];
}

export const AssistantMessage = ({
  turn,
  isLastInGroup = true,
  groupCitations = [],
}: Props) => {
  const { openCitation } = useCitations();
  const { sessionId } = useChatSession();

  const resolvedIds = useMemo(
    () => new Set(groupCitations.map((c) => c.id)),
    [groupCitations],
  );

  const citationsByDocId = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of groupCitations) {
      if (c.doc_id) map.set(c.doc_id, c.id);
    }
    return map;
  }, [groupCitations]);

  const handleCitationClick = useCallback(
    (id: number) => {
      const found = groupCitations.find((c) => c.id === id);
      if (found) openCitation(found, groupCitations);
    },
    [groupCitations, openCitation],
  );

  const fadeTokens = !!(turn.isStreaming || turn.fadeHold);

  const hasContent = turn.items?.some(
    (i) => i.type === "text" || i.type === "tool_call",
  );
  const hasActiveTools = turn.items?.some(
    (i) =>
      i.type === "tool_call" &&
      (i.toolCall.status === "pending" || i.toolCall.status === "running"),
  );

  const mapProgress = useMapProgress(sessionId, !!hasActiveTools);

  const progressOwnerIdRef = useRef<string | null>(null);
  if (mapProgress && hasActiveTools && !progressOwnerIdRef.current) {
    const active = turn.items?.find(
      (i) =>
        i.type === "tool_call" &&
        (i.toolCall.status === "pending" || i.toolCall.status === "running"),
    );
    if (active && active.type === "tool_call") {
      progressOwnerIdRef.current = active.toolCall.id;
    }
  } else if (!mapProgress && !hasActiveTools) {
    progressOwnerIdRef.current = null;
  }

  const allContentText = useMemo(
    () => getTurnCitationText(turn),
    [turn.items, turn.finalResponse, turn.content],
  );

  const renderItems = () => {
    if (!turn.items) return null;

    const elements: React.ReactNode[] = [];
    let toolGroup: React.ReactNode[] = [];

    const flushToolGroup = () => {
      if (toolGroup.length > 0) {
        elements.push(
          <div key={`tool-group-${elements.length}`} className="my-1">
            {toolGroup}
          </div>,
        );
        toolGroup = [];
      }
    };

    for (let i = 0; i < turn.items.length; i++) {
      const item = turn.items[i];
      if (item.type === "text") {
        flushToolGroup();
        elements.push(
          <div key={i}>
            <ChatMarkdown
              source={prepareMathMarkdown(
                cleanTextForDisplay(item.content, turn.isStreaming, sessionId),
              )}
            />
          </div>,
        );
      } else if (item.type === "tool_call") {
        const isProgressOwner = item.toolCall.id === progressOwnerIdRef.current;
        toolGroup.push(
          <ToolCallItem
            key={item.toolCall.id}
            toolCall={item.toolCall}
            mapProgress={isProgressOwner ? mapProgress : undefined}
          />,
        );
      }
    }
    flushToolGroup();

    return elements;
  };

  return (
    <ChatMarkdownProvider
      onCitationClick={handleCitationClick}
      resolvedIds={resolvedIds}
      citationsByDocId={citationsByDocId}
      fadeTokens={fadeTokens}
    >
      <div
        role="article"
        aria-label="Assistant response"
        className="chat-prose text-base leading-7 text-zinc-900"
      >
        {turn.isStreaming && !hasContent && (
          <ResponseSkeleton lines={3} label="Responding" className="mb-3" />
        )}

        {renderItems()}

        {turn.finalResponse && (
          <ChatMarkdown
            source={prepareMathMarkdown(
              cleanTextForDisplay(turn.finalResponse, false, sessionId),
            )}
          />
        )}

        {!turn.isStreaming &&
          !turn.finalResponse &&
          turn.items?.length === 0 && (
            <span className="text-xs text-zinc-500">Stopped</span>
          )}

        {!turn.isStreaming && isLastInGroup && allContentText && (
          <MessageActionsBar content={allContentText} />
        )}
      </div>
    </ChatMarkdownProvider>
  );
};
